import React from 'react';
import { getSpec } from '@services/scoreSpecs';
import { specIdForFeed } from './specForFeed';

// Fetches the spec behind the feed on screen, lazily.
//
// Nothing is requested until the panel is opened, because most reads of a feed are not questions
// about how it was authored — and the spec API is a separate service from the one that returned
// the rows, so a request here is a real round trip rather than a free extra field.
//
// Cached per spec id for the session. A locked spec is immutable, which is what makes caching safe
// rather than merely convenient: the prompt cannot change under a cache entry, so drilling between
// members and back never re-fetches.

const cache = new Map();

// Exported for tests, which would otherwise leak a spec between cases.
export const clearFeedPromptCache = () => cache.clear();

export const useFeedPrompt = (feedId, { enabled = true } = {}) => {
  const specId = specIdForFeed(feedId);

  const [isOpen, setIsOpen] = React.useState(false);
  const [state, setState] = React.useState({ isLoading: false, spec: null, error: null });

  const cancelRef = React.useRef({ isCanceled: false });
  React.useEffect(() => {
    const token = cancelRef.current;
    return () => {
      token.isCanceled = true;
    };
  }, []);

  // Monotonic, the way useFeedView guards its own reads. The unmount flag above is NOT enough on its
  // own: it flips only on unmount, and a drill-down does not unmount this hook — it re-renders it
  // with a different feed. Without a token, opening the panel on a slow feed A, drilling to B and
  // opening B's panel leaves A's fetch free to land last and paint A's spec id and A's prompt above
  // B's scores. That is the one state this panel exists to rule out.
  const requestIdRef = React.useRef(0);

  // A new feed closes the panel and drops what was on screen. Leaving the previous spec visible
  // while the rows underneath belong to a different feed is the one wrong state worth designing
  // out: the whole point is to say what produced THESE rows.
  React.useEffect(() => {
    // Bumped here as well as in `load`, so a fetch already in flight for the PREVIOUS spec is
    // superseded by the feed change itself rather than only by a competing fetch.
    requestIdRef.current += 1;
    setIsOpen(false);
    setState({ isLoading: false, spec: null, error: null });
  }, [specId]);

  const load = React.useCallback(async () => {
    if (!specId) return;

    if (cache.has(specId)) {
      setState({ isLoading: false, spec: cache.get(specId), error: null });
      return;
    }

    requestIdRef.current += 1;
    const id = requestIdRef.current;
    // Captured, not re-read after the await: `specId` is closed over per `load` identity, but
    // naming it here makes the comparison below state what it is actually checking.
    const forSpecId = specId;

    setState({ isLoading: true, spec: null, error: null });
    const { spec, error } = await getSpec(specId);
    if (cancelRef.current.isCanceled) return;
    // Both halves matter. The id rules out a newer request for the same spec; the spec comparison
    // rules out a request that was current when it left and is not the feed on screen now.
    if (requestIdRef.current !== id || forSpecId !== specId) return;

    if (spec) {
      cache.set(specId, spec);
      setState({ isLoading: false, spec, error: null });
    } else {
      setState({ isLoading: false, spec: null, error });
    }
  }, [specId]);

  const toggle = React.useCallback(() => {
    setIsOpen((wasOpen) => {
      if (!wasOpen) load();
      return !wasOpen;
    });
  }, [load]);

  return {
    // The affordance renders only when there is a spec to reach AND the caller says the account
    // may reach it. Offering a control that can only fail teaches nothing.
    isAvailable: !!specId && enabled,
    specId,
    isOpen,
    toggle,
    retry: load,
    ...state,
  };
};
